import type {
  BaseSelection,
  GameLogicContent,
  GameLogicModifiers,
  GameLogicRequirements,
  GameLogicState,
  GameLogicTypes,
  SlideGeneric,
} from "@types"
import GAMEDATA from "../features/default"

// Terminal slide shown when the run ends — either by the age rule or because
// no valid slide remains. Empty selections make SlideViewer render GameSummary.
export const END_OF_GAME_SLIDE: SlideGeneric = {
  title: { en: `What an adventure!` },
  description: {
    en: "Try again to see where you end up next time.",
  },
  image: "/images/stop_slide-1024.jpg",
  imageSrcSet: "/images/stop_slide-1024.jpg 1024w",
  imageAlt: "slide alt text",
  type: "event",
  selections: [],
}

// Function to check if the current value meets the given requirement (gt, lt, eq, not)
const meetsRequirement = (
  value: number,
  requirement?: GameLogicTypes,
): boolean => {
  if (requirement === undefined) return true
  if ("gt" in requirement && value < requirement.gt) return false
  if ("lt" in requirement && value > requirement.lt) return false
  if ("eq" in requirement && value !== requirement.eq) return false
  if ("not" in requirement && value === requirement.not) return false
  if (
    "gt" in requirement &&
    "lt" in requirement &&
    (value < requirement.gt || value > requirement.lt)
  )
    return false

  return true
}

// Function to check tag requirements
const meetsTagRequirements = (
  tags: string[],
  requirementTags?: {
    not?: string[]
    or?: string[]
    and?: string[]
    has?: string
  },
) => {
  if (!requirementTags) return true
  if (
    requirementTags.not &&
    requirementTags.not.some(tag => tags.includes(tag))
  )
    return false
  if (
    requirementTags.and &&
    !requirementTags.and.every(tag => tags.includes(tag))
  )
    return false
  if (requirementTags.or && !requirementTags.or.some(tag => tags.includes(tag)))
    return false
  if (requirementTags.has && !tags.includes(requirementTags.has)) return false

  return true
}

const processRequirements = (
  requirements: GameLogicRequirements | undefined,
  currentState: GameLogicState,
) => {
  const {
    age,
    education,
    health,
    relationship,
    mentalHealth,
    physicalHealth,
    tags,
  } = currentState
  if (requirements) {
    if (!meetsRequirement(age, requirements.age)) return false
    if (!meetsRequirement(education, requirements.education)) return false
    if (!meetsRequirement(health, requirements.health)) return false
    if (!meetsRequirement(relationship, requirements.relationship)) return false
    if (!meetsRequirement(mentalHealth, requirements.mentalHealth)) return false
    if (!meetsRequirement(physicalHealth, requirements.physicalHealth))
      return false
    if (!meetsTagRequirements(tags, requirements.tags)) return false
  }
  return true
}

export const getValidTriggers = (
  input: GameLogicContent,
  currentState: GameLogicState,
) => {
  const allValidTriggers = input.flatMap(deck => {
    if (!processRequirements(deck.requirements, currentState)) return []
    return deck.groups.flatMap(group => {
      const { requirements, slides } = group
      const isValidEntry = processRequirements(requirements, currentState)
      if (isValidEntry) {
        return slides
          ?.filter(trigger => trigger.type === "trigger")
          .filter(trigger =>
            processRequirements(trigger.requirements, currentState),
          )
      }
      return []
    })
  })
  const allTitles = currentState.history
    .map(h => h.slide)
    .filter(s => s !== undefined)
  return allValidTriggers.filter(
    t => t !== undefined && !allTitles.includes(t.title.en),
  )
}

// Main Function to Filter and Select a Slide
export const getValidSlides = (
  decks: GameLogicContent,
  currentState: GameLogicState,
) => {
  // Filter all slides that meet the requirements
  const allValidSlides = decks.flatMap(deck => {
    if (!processRequirements(deck.requirements, currentState)) return []
    return deck.groups.flatMap(entry => {
      const { requirements, slides } = entry
      const isValidEntry = processRequirements(requirements, currentState)
      if (isValidEntry) {
        return slides
          .filter(slide => slide.type !== "trigger")
          .filter(slide =>
            processRequirements(slide.requirements, currentState),
          )
      }
      return []
    })
  })
  const allTitles = currentState.history
    .map(h => h.slide)
    .filter(s => s !== undefined)
  return allValidSlides.filter(
    slides => slides !== undefined && !allTitles.includes(slides.title.en),
  )
}

export const getValidRandomTrigger = (
  input: GameLogicContent,
  currentState: GameLogicState,
) => {
  const allValidTriggers = getValidTriggers(input, currentState)

  // Randomly select one slide from the valid Triggers
  if (allValidTriggers.length === 0) return null

  const randomIndex =
    crypto.getRandomValues(new Uint32Array(1))[0] % allValidTriggers.length
  return allValidTriggers[randomIndex]
}

export const getRandomValidSlide = (
  input: GameLogicContent,
  currentState: GameLogicState,
) => {
  const allValidSlides = getValidSlides(input, currentState)

  // Randomly select one slide from the valid slides
  if (allValidSlides.length === 0) return null

  const randomIndex =
    crypto.getRandomValues(new Uint32Array(1))[0] % allValidSlides.length
  return allValidSlides[randomIndex]
}

export const getRandomValidTriggerOrSlide = (
  input: GameLogicContent,
  currentState: GameLogicState,
) => {
  const trigger = getValidRandomTrigger(input, currentState)
  if (trigger) return trigger
  return getRandomValidSlide(input, currentState)
}

// Display labels for the meters guarded by the mentor gate, matching the
// meter names shown on the game screen (GameSlidePage renders exactly these
// three). Attributes without an on-screen meter (health, relationship,
// agency) are not gated: no game content ever decrements them, and the
// mentor popup must only ever name a meter the player can see.
const METER_LABELS = {
  education: "Education",
  mentalHealth: "Happiness",
  physicalHealth: "Health",
} as const

type GatedMeter = keyof typeof METER_LABELS

// Check if any meter has reached zero (requires mentor intervention)
export const checkForZeroMeters = (currentState: GameLogicState) =>
  (Object.keys(METER_LABELS) as GatedMeter[])
    .filter(meter => currentState[meter] <= 0)
    .map(meter => METER_LABELS[meter])

// Meters that this turn pushed to zero (requires mentor intervention).
// Meters that were already at zero before the turn are excluded, so a meter
// resting at zero doesn't interrupt every following turn — but each further
// meter reaching zero does, as does the same meter recovering and dropping
// back down.
export const checkForNewZeroMeters = (
  previousState: GameLogicState,
  currentState: GameLogicState,
) => {
  const alreadyAtZero = checkForZeroMeters(previousState)
  return checkForZeroMeters(currentState).filter(
    meter => !alreadyAtZero.includes(meter),
  )
}

// Meters that must pause the game for a mentor code this turn: every meter
// newly at/below zero, plus every already-zeroed meter this choice hit with a
// further negative effect. processTurn floors meters at zero, so a repeat hit
// is invisible in the post-turn state — it can only be detected from the
// pre-turn values and the selection's effect signs. A meter merely resting at
// zero (no negative effect this turn) does not re-prompt.
export const checkForMentorGateMeters = (
  previousState: GameLogicState,
  currentState: GameLogicState,
  selection: BaseSelection,
) => {
  const newlyAtZero = checkForNewZeroMeters(previousState, currentState)
  // Disjoint from newlyAtZero: these meters were already at/below zero.
  const repeatHits = (Object.keys(METER_LABELS) as GatedMeter[])
    .filter(
      meter =>
        previousState[meter] <= 0 && (selection.effects?.[meter] ?? 0) < 0,
    )
    .map(meter => METER_LABELS[meter])
  return [...newlyAtZero, ...repeatHits]
}

// Check game rules
export const checkGameRules = (
  currentState: GameLogicState,
  rules: GameLogicRequirements,
) => {
  const { age, education, health, relationship, mentalHealth, physicalHealth } =
    currentState
  if (!meetsRequirement(age, rules.age)) return false
  if (!meetsRequirement(education, rules.education)) return false
  if (!meetsRequirement(health, rules.health)) return false
  if (!meetsRequirement(relationship, rules.relationship)) return false
  if (!meetsRequirement(mentalHealth, rules.mentalHealth)) return false
  if (!meetsRequirement(physicalHealth, rules.physicalHealth)) return false
  return true
}

// Check selection consquences
export const processTurn = (
  currentState: GameLogicState,
  selection: BaseSelection,
) => {
  const newState = { ...currentState }
  newState.turn += 1
  newState.age += GAMEDATA.start.effects.age ?? 1

  if (selection.effects) {
    if (selection.effects.age) newState.age += selection.effects.age

    // Limit the values to 0 or above
    if (selection.effects.education)
      newState.education = Math.max(
        0,
        newState.education + selection.effects.education,
      )
    if (selection.effects.health)
      newState.health = Math.max(0, newState.health + selection.effects.health)
    if (selection.effects.relationship)
      newState.relationship = Math.max(
        0,
        newState.relationship + selection.effects.relationship,
      )
    if (selection.effects.mentalHealth)
      newState.mentalHealth = Math.max(
        0,
        newState.mentalHealth + selection.effects.mentalHealth,
      )
    if (selection.effects.physicalHealth)
      newState.physicalHealth = Math.max(
        0,
        newState.physicalHealth + selection.effects.physicalHealth,
      )
  }

  if (selection.effects?.tags) {
    if (selection.effects.tags.add) {
      newState.tags = [...newState.tags, ...selection.effects.tags.add]
    }
    if (selection.effects.tags.remove) {
      newState.tags = newState.tags.filter(
        tag => !selection?.effects?.tags?.remove?.includes(tag),
      )
    }
  }

  // Calculate total impact of this choice
  const totalImpact =
    (selection.effects?.agency ?? 0) +
    (selection.effects?.education ?? 0) +
    (selection.effects?.health ?? 0) +
    (selection.effects?.relationship ?? 0) +
    (selection.effects?.mentalHealth ?? 0) +
    (selection.effects?.physicalHealth ?? 0)

  newState.history = [
    ...(currentState?.history ?? []),
    {
      slide: currentState.slide?.title.en ?? "unknown",
      action: selection.title.en,
      effects: selection.effects,
      totalImpact: totalImpact,
    },
  ]

  return newState
}

// Selection is random among valid slides, so allow a few attempts — but never
// spin: when no valid slide exists the search returns null every time, and the
// run must end gracefully instead of hanging the UI.
const MAX_SLIDE_TRIES = 10

export const pickNextSlide = (
  updated: GameLogicState,
  content: GameLogicContent = GAMEDATA.content,
  rules: GameLogicRequirements = GAMEDATA.rules,
  endSlide: SlideGeneric = END_OF_GAME_SLIDE,
): SlideGeneric => {
  if (!checkGameRules(updated, rules)) return endSlide
  for (let tries = 0; tries < MAX_SLIDE_TRIES; tries += 1) {
    const slide = getRandomValidTriggerOrSlide(content, updated)
    if (slide) return slide
  }
  return endSlide
}

export type TurnPlan = {
  /** Game state after the selection's effects */
  updated: GameLogicState
  /** Meters that must pause the game for a mentor code (see checkForMentorGateMeters) */
  gatedMeters: string[]
  /** The slide to show next (or the end-of-game slide) */
  nextSlide: SlideGeneric
  /** The selection's effects, for the meter feedback */
  effects?: GameLogicModifiers
}

// Everything a choice does, computed up front and without touching the store:
// pure apart from the random slide pick, so it can run when a slide is shown
// (to pre-decode the next picture) as well as at tap time.
export const planTurn = (
  state: GameLogicState,
  selection: BaseSelection,
  content: GameLogicContent = GAMEDATA.content,
  rules: GameLogicRequirements = GAMEDATA.rules,
): TurnPlan => {
  const updated = processTurn(state, selection)
  const gatedMeters = checkForMentorGateMeters(state, updated, selection)
  const nextSlide = pickNextSlide(updated, content, rules)
  return { updated, gatedMeters, nextSlide, effects: selection.effects }
}
